import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn, Switch, Tooltip } from '@c2fo/liquidity';
import { getOfferBuilderNavigationState } from '@/enterprise/components/OfferActivity/OfferActivityCards';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useTurnOffParticipation } from '@/enterprise/data/useTurnOffParticipation';
import { useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { useReporting } from '@/reporting';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useRestrictions from '@/utils/useRestrictions';
import TurnOffParticipationModal from './TurnOffParticipationModal';

const ParticipationSwitch = ({ takerMarket, withLabel = false }: { takerMarket: TakerMarket; withLabel?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getRestrictions } = useRestrictions();
  const { trackEnterpriseEvent } = useReporting();
  const { openModal } = useOfferModalContext();
  const enableUnification = useEnableUnification();
  const { canEditOffers } = getRestrictions([takerMarket]);
  const { turnOffParticipation, isLoading: turnOffParticipationIsLoading } = useTurnOffParticipation(takerMarket);
  const hasOfferSchedule = !!takerMarket.offerSchedule;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const inactiveMarket = !takerMarket.marketIsEnabled && !takerMarket.offerConfig.isEnabled; // can only turn participation off if market is disabled
  const toggleDisabled =
    !canEditOffers ||
    turnOffParticipationIsLoading ||
    takerMarket.offerConfig.isManuallyAwarded ||
    takerMarket.offerConfig.isActiveExpressAccept ||
    inactiveMarket;

  const onClick = async () => {
    // show confirmation modal if market is disabled
    if (!takerMarket.marketIsEnabled) {
      setShowConfirmModal(true);
      return;
    }

    // toggle participation off using centralized hook
    if (takerMarket.offerConfig.isEnabled) {
      return await turnOffParticipation();
    }

    // when toggling market participation on, use offer modal and track events
    const eventType =
      takerMarket.marketPricingType === 'BENCHMARK'
        ? 'offer::variable-rate::clicked'
        : takerMarket.marketPricingType === 'STATIC'
        ? 'offer::fixed-rate::clicked'
        : 'offer::clicked';

    trackEnterpriseEvent(eventType);

    if (!enableUnification) {
      return openModal({ takerMarket, referrer: 'participation' });
    } else {
      const navigationState = getOfferBuilderNavigationState([takerMarket], { expandSelected: true });
      return navigate(asEnterpriseLink(`build-offer`), { state: navigationState });
    }
  };

  const handleConfirmTurnOff = async () => {
    await turnOffParticipation();
    setShowConfirmModal(false);
  };

  const handleCancelTurnOff = () => {
    setShowConfirmModal(false);
  };

  const accessibilityLabel = t('core.toggleParticipation');
  const isChecked = takerMarket.offerConfig.isEnabled ?? false;

  // handle inactive market
  if (inactiveMarket) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-block">
            <Switch accessibilityLabel={accessibilityLabel} disabled />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            arrow
            className="max-w-60 whitespace-pre-wrap text-center"
            collisionPadding={24}
            side="bottom"
            variant="dark"
          >
            {t('core.closedMarketTooltip')}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip>
    );
  }

  // handle preferred
  // disabled & checked
  if (takerMarket.offerConfig.offerType === 'PREFERRED_TERM') {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-block">
            <Switch accessibilityLabel={accessibilityLabel} checked disabled />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            arrow
            className="max-w-60 whitespace-pre-wrap text-center"
            collisionPadding={24}
            side="bottom"
            variant="dark"
          >
            {t('takerMarketTabs.preferredRate.switchTooltip')}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip>
    );
  }

  // handle offer scheduling
  // disabled & checked based on if offer is enabled
  if (hasOfferSchedule) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-block">
            <Switch accessibilityLabel={accessibilityLabel} checked={isChecked} disabled />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            arrow
            className="max-w-60 whitespace-pre-wrap text-center"
            collisionPadding={24}
            side="bottom"
            variant="dark"
          >
            {t('scheduling.lockedParticipation')}{' '}
            <span className="italic">{t('scheduling.schedulesOperateUsCentral')}</span>
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip>
    );
  }

  // handle early market processing or active express-accept
  // disabled & checked based on if offer is enabled
  if (takerMarket.offerConfig.isManuallyAwarded || takerMarket.offerConfig.isActiveExpressAccept) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="flex items-center gap-2">
            <Switch accessibilityLabel={accessibilityLabel} checked={isChecked} disabled />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content arrow side="top" className="z-50 max-w-60 whitespace-pre-wrap text-center" variant="dark">
            {takerMarket.offerConfig.offerType === 'EXPRESS_ACCEPT'
              ? t('core.expressAcceptProcessingPopover')
              : t('core.marketIsEarlyProcessingPopover')}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip>
    );
  }

  return (
    <>
      <span className="flex items-center gap-2">
        <Switch
          accessibilityLabel={accessibilityLabel}
          checked={isChecked}
          disabled={toggleDisabled}
          loading={turnOffParticipationIsLoading}
          onClick={onClick}
        />
        {withLabel && (
          <span className={cn('font-bold', { 'opacity-38': toggleDisabled })}>
            {isChecked ? t('scheduling.on') : t('taker.globalOfferParticipationOff')}
          </span>
        )}
      </span>
      <TurnOffParticipationModal
        open={showConfirmModal}
        onClose={handleCancelTurnOff}
        onConfirm={handleConfirmTurnOff}
        takerMarket={takerMarket}
      />
    </>
  );
};

export default ParticipationSwitch;
